import { useTranslation } from "react-i18next"

import { Card, CardContent } from "@/components/ui/card"
import { cn } from "@/lib/utils"

/**
 * OKS-305 — Slack-style canonical OG unfurl preview tile.
 *
 * Single representative tile (NOT per-platform — PRD §7 Q4). Every
 * messaging app renders OG differently; mimicking each one in our UI
 * is a rabbit hole and never accurate. One canonical tile + a caption
 * ("Preview — actual rendering varies by app") sets the right
 * expectation for the admin.
 *
 * Live preview = pure React state in the parent page (no Inertia
 * partial reload, no debounce — TRD §K10 + UX Spec §4). The tile
 * receives the already-resolved values and just renders them.
 *
 * `aria-live="polite"` so screen readers hear the title/description
 * update once typing pauses (UX Spec §6).
 *
 * Brand-isolation: all props come from server-rendered values + the
 * admin's own keystrokes. No URLs or props are derived from anything
 * but `Current.brand`.
 */
export interface LinkSharingPreviewTileProps {
  /** Resolved title (custom OR i18n fallback applied server-side). */
  title: string
  /** Resolved description (custom OR i18n fallback). */
  description: string
  /** Resolved image URL — never null (deepest fallback is the
   *  platform-default `/og-default.png` absolute URL). */
  imageUrl: string
  /** Brand name — used as the `<img alt>` attribute. */
  brandName: string
  /** Domain string shown under the image, mimicking the Slack/WhatsApp
   *  unfurl chrome (e.g. `crew.oks.social`). */
  domain: string
  /** Optional className passthrough. */
  className?: string
}

// Testid contract (locked with LQE's Playwright spec
// `tests/playwright/brand-link-sharing.spec.ts`):
//   container         link-sharing-preview-tile
//   image (img el)    link-sharing-preview-image
//   title (p el)      link-sharing-preview-title
//   description (p)   link-sharing-preview-description
//   domain (p)        link-sharing-preview-domain
//   caption (p)       link-sharing-preview-caption
// Hardcoded (not derived from a testId prop) so the contract is grep-
// stable across the spec / component / Vitest.
export function LinkSharingPreviewTile({
  title,
  description,
  imageUrl,
  brandName,
  domain,
  className,
}: LinkSharingPreviewTileProps) {
  const { t } = useTranslation()

  return (
    <div
      data-testid="link-sharing-preview-tile"
      aria-live="polite"
      className={cn("space-y-2", className)}
    >
      <p className="text-muted-foreground text-xs font-medium">
        {t("settings.brand.link_sharing.preview_heading")}
      </p>
      <Card className="overflow-hidden p-0 shadow-sm">
        {/* Image at the top, OG canonical aspect 1.91:1. `object-cover`
            so non-conforming uploads still fill the tile (the resolver
            ships static 1200/630 width/height regardless). */}
        <div className="bg-muted aspect-[1200/630] w-full overflow-hidden">
          <img
            src={imageUrl}
            alt={brandName}
            className="h-full w-full object-cover"
            data-testid="link-sharing-preview-image"
          />
        </div>
        <CardContent className="space-y-1 px-3 py-3">
          <p
            className="text-muted-foreground text-xs tracking-wide uppercase"
            data-testid="link-sharing-preview-domain"
          >
            {domain}
          </p>
          {/* Title: bold + two-line clamp (UX Spec §2.1). */}
          <p
            className="line-clamp-2 text-sm leading-snug font-semibold"
            data-testid="link-sharing-preview-title"
          >
            {title}
          </p>
          {/* Description: muted + three-line clamp. */}
          <p
            className="text-muted-foreground line-clamp-3 text-xs leading-snug"
            data-testid="link-sharing-preview-description"
          >
            {description}
          </p>
        </CardContent>
      </Card>
      <p
        className="text-muted-foreground text-xs"
        data-testid="link-sharing-preview-caption"
      >
        {t("settings.brand.link_sharing.preview_caption")}
      </p>
    </div>
  )
}

export default LinkSharingPreviewTile
